import { useCallback, useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

// Menu items are derived from upsells (upgrades without the 'hasPackage' tag):
// an upsell "is a menu item" for a branch iff that branch id is present in its
// hasAsMenuItem array. This hook wraps the dedicated /api/v1/menuItem endpoints.
const useMenuItem = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  // List all upsells together with their current menu-item branch assignment.
  const getMenuItems = useCallback(
    async (search = '') => {
      const params = {};
      if (search) params.search = search;
      return await sendRequest({
        url: '/api/v1/menuItem',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params,
      });
    },
    [authCtx, sendRequest]
  );

  // Set the full list of branches a single upsell is offered as a menu item for.
  // For full-right callers this replaces the list; for assigned-branch ("own")
  // callers the server only applies the caller's own branches and preserves the
  // rest.
  const updateMenuItemBranches = useCallback(
    async (upgradeId, branchIds) => {
      return await sendRequest({
        url: `/api/v1/menuItem/${upgradeId}`,
        method: 'PATCH',
        headers: authCtx.getAuthHeaders(),
        body: { branchIds: Array.isArray(branchIds) ? branchIds : [] },
      });
    },
    [authCtx, sendRequest]
  );

  return { isLoading, error, getMenuItems, updateMenuItemBranches };
};

export default useMenuItem;
